/**
 * GpsPinPicker — click-to-drop GPS pin for KYC.
 *
 * Wraps the existing react-leaflet MapContainer (already in admin-web
 * for DeviceLocationMap). On click the parent receives
 * { lat, lng, accuracyM }. accuracyM is null when the pin is
 * manually dropped (admin entered a guess); only the browser
 * geolocation API populates a real number.
 *
 * Default centre: TZ middle (-6.37, 34.89). Override via the
 * `defaultCenter` prop when a country expands beyond TZ.
 */
import { useEffect, useState } from "react";
import { CircleMarker, MapContainer, TileLayer, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "./Button.js";

export interface GpsPin {
  readonly lat: number;
  readonly lng: number;
  readonly accuracyM: number | null;
}

interface Props {
  readonly label?: string;
  readonly value: GpsPin | null;
  readonly onChange: (pin: GpsPin | null) => void;
  /** Map centre + initial zoom. Defaults to mid-TZ at 6. */
  readonly defaultCenter?: { lat: number; lng: number; zoom: number };
}

const TZ_DEFAULT = { lat: -6.37, lng: 34.89, zoom: 6 };

export function GpsPinPicker({
  label = "GPS pin",
  value,
  onChange,
  defaultCenter = TZ_DEFAULT,
}: Props): JSX.Element {
  const [browserError, setBrowserError] = useState<string | null>(null);

  function useBrowserLocation(): void {
    setBrowserError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setBrowserError("Browser geolocation not available.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        onChange({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy,
        });
      },
      (err) => setBrowserError(err.message),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  const centre = value ? { lat: value.lat, lng: value.lng } : defaultCenter;

  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <p className="text-xs font-medium text-slate-700">{label}</p>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={useBrowserLocation}>
            Use my location
          </Button>
          {value ? (
            <Button variant="secondary" onClick={() => onChange(null)}>
              Clear
            </Button>
          ) : null}
        </div>
      </div>
      <div className="h-64 overflow-hidden rounded-md border border-slate-200">
        <MapContainer
          center={[centre.lat, centre.lng]}
          zoom={defaultCenter.zoom}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{y}/{x}.png"
          />
          <ClickHandler
            onClick={(latlng) => onChange({ lat: latlng.lat, lng: latlng.lng, accuracyM: null })}
          />
          {value ? (
            <>
              <CircleMarker
                center={[value.lat, value.lng]}
                radius={6}
                pathOptions={{ color: "#1d4ed8", fillColor: "#3b82f6", fillOpacity: 0.8 }}
              />
              {value.accuracyM !== null ? (
                <CircleMarker
                  center={[value.lat, value.lng]}
                  radius={Math.min(120, Math.max(8, value.accuracyM / 10))}
                  pathOptions={{ color: "#3b82f6", fillOpacity: 0.05, weight: 1 }}
                />
              ) : null}
            </>
          ) : null}
          {value ? <RecenterTo lat={value.lat} lng={value.lng} /> : null}
        </MapContainer>
      </div>
      {value ? (
        <p className="text-[11px] text-slate-500">
          {value.lat.toFixed(5)}, {value.lng.toFixed(5)}
          {value.accuracyM !== null ? ` · ±${Math.round(value.accuracyM)} m` : " · manual"}
        </p>
      ) : (
        <p className="text-[11px] text-slate-500">
          Click on the map to drop a pin or use "Use my location" for the browser GPS fix.
        </p>
      )}
      {browserError ? <p className="text-xs text-red-700">{browserError}</p> : null}
    </div>
  );
}

function ClickHandler({
  onClick,
}: {
  onClick: (latlng: { lat: number; lng: number }) => void;
}): null {
  useMapEvents({
    click(e) {
      onClick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function RecenterTo({ lat, lng }: { lat: number; lng: number }): null {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], Math.max(map.getZoom(), 13), { duration: 0.6 });
  }, [lat, lng, map]);
  return null;
}
